import React, {useState} from 'react';
import {ActionButton, Link, mergeStyles, PrimaryButton, Stack, Text, useTheme} from '@fluentui/react';
import {toast} from 'react-toastify';
import {useGetStorefrontProductSuggestionsQuery} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {useBulkListOnStorefrontMutation} from '../../store/Api';
import {IStorefrontSuggestedProduct} from '../../models/Storefront';
import useCan, {STOREFRONT_ADMIN} from '../../permissions/ability';
import CurrencyDisplay from '../Currency/CurrencyDisplay';
import DetailsModal from '../DetailsModal/DetailsModal';
import SuggestedProductsPanel from './SuggestedProductsPanel';
import SuggestionReasonChip from './SuggestionReasonChip';
import {useFittedCardCount} from './useFittedCardCount';
import {
    addErrorMessage,
    ISuggestionListingEntry,
    listingFailureMessage,
    multiListingBody,
    multiListingSummary,
    suggestionAmountState,
    suggestionDetail,
    suggestionListingBody,
    suggestionReason,
    SUGGESTION_SOURCES,
    SUGGESTION_STRIP_SIZE,
} from './suggestedProductsLogic';

const ADD_FAILED = 'We could not add that product to the store.';
const ADD_MANY_FAILED = 'We could not add those products to the store.';
const NO_CATALOG = 'There is no product catalog on this organization yet, so there is nothing to add.';

/** Card geometry, in px so the fit arithmetic is exact against a measured pixel width. */
const CARD_WIDTH = 260;
const CARD_GAP = 12;

interface ISuggestedProductsStripProps {
    storefront: string;
    /** Products were listed. The products grid is untagged, so the host refetches it itself. */
    onProductsAdded: () => void;
}

/** The product to open the shared details modal on, and the catalog its id belongs to. */
interface IDetailsTarget {
    id: string;
    catalog: string;
}

/**
 * One compact card in the strip: what it is, what it would cost, and a single click that shelves it.
 *
 * Only the cost and the Add button carry any weight. Everything else on the card repeats down the row, so the
 * reason chip and the stock state are said quietly and the eye is left with the one figure that differs.
 *
 * The thumbnail is dropped entirely rather than replaced with a placeholder tile when the product has no
 * image, because a row of identical grey squares says nothing and the text is better off with the width.
 */
const SuggestionCard: React.FC<{
    product: IStorefrontSuggestedProduct;
    pending: boolean;
    disabled: boolean;
    onAdd: () => void;
    onOpenDetails?: () => void;
}> = ({product, pending, disabled, onAdd, onOpenDetails}) => {
    const theme = useTheme();
    const detail = suggestionDetail(product);
    const costState = suggestionAmountState(product.cost, product.hidePrice);
    // A class rather than inline style so the hover lift is a real CSS selector instead of mouse-event state.
    const cardClass = mergeStyles({
        width: CARD_WIDTH,
        flexShrink: 0,
        padding: 12,
        borderRadius: 8,
        border: `1px solid ${theme.palette.neutralLighter}`,
        background: theme.palette.white,
        transition: 'box-shadow .15s ease, border-color .15s ease',
        selectors: {
            ':hover': {
                boxShadow: theme.effects.elevation8,
                borderColor: theme.palette.neutralTertiaryAlt,
            },
        },
    });
    return (
        <Stack className={cardClass} tokens={{childrenGap: 6}}>
            <Stack horizontal verticalAlign='center' tokens={{childrenGap: '0.5em'}}>
                {product.imageUrl && (
                    <img
                        src={product.imageUrl}
                        alt=''
                        onClick={onOpenDetails}
                        style={{
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                            objectFit: 'contain',
                            borderRadius: 4,
                            cursor: onOpenDetails ? 'pointer' : undefined,
                        }}
                    />
                )}
                <Stack grow styles={{root: {minWidth: 0}}}>
                    <Link
                        onClick={onOpenDetails}
                        disabled={!onOpenDetails}
                        title={product.name}
                        styles={{root: {
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                        }}}
                    >
                        {product.name ?? 'Untitled'}
                    </Link>
                    {detail && (
                        <Text
                            variant='small'
                            block
                            nowrap
                            title={detail}
                            styles={{root: {
                                color: theme.palette.neutralSecondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}}
                        >
                            {detail}
                        </Text>
                    )}
                </Stack>
            </Stack>

            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                <Stack.Item styles={{root: {minWidth: 0}}}>
                    <SuggestionReasonChip reason={suggestionReason(product)}/>
                </Stack.Item>
                {/* A dot and a word rather than a filled badge: stock is context here, not the headline. */}
                <Stack
                    horizontal
                    verticalAlign='center'
                    tokens={{childrenGap: 4}}
                    styles={{root: {flexShrink: 0, whiteSpace: 'nowrap'}}}
                >
                    <span
                        style={{
                            width: 8,
                            height: 8,
                            flexShrink: 0,
                            borderRadius: '50%',
                            background: product.inStock ? theme.palette.green : theme.palette.red,
                        }}
                    />
                    <Text variant='small' nowrap styles={{root: {color: theme.palette.neutralSecondary}}}>
                        {product.inStock ? 'In stock' : 'Out of stock'}
                    </Text>
                </Stack>
            </Stack>

            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                {costState === 'value' ? (
                    <Stack
                        horizontal
                        verticalAlign='baseline'
                        tokens={{childrenGap: 4}}
                        styles={{root: {minWidth: 0}}}
                        title='Best vendor cost for this store'
                    >
                        <Text styles={{root: {
                            fontSize: 16,
                            fontWeight: 600,
                            color: theme.palette.neutralPrimary,
                        }}}>
                            <CurrencyDisplay value={product.cost}/>
                        </Text>
                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>cost</Text>
                    </Stack>
                ) : (
                    <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                        {costState === 'hidden' ? 'Priced by quote' : 'No cost'}
                    </Text>
                )}
                <PrimaryButton
                    text={pending ? 'Adding...' : 'Add'}
                    title={`Add ${product.name ?? 'this product'} to the store`}
                    disabled={disabled || pending}
                    onClick={onAdd}
                    styles={{root: {height: 28, minWidth: 0, padding: '0 14px', borderRadius: 4}}}
                />
            </Stack>
        </Stack>
    );
};

/**
 * "You sell these a lot, and your other stores already carry them. Put them on this one."
 *
 * A low-profile prompt above the products grid, built from the organization's imported won opportunities, what
 * its other storefronts already list, what sells beside this store's own products, and the categories its
 * stores are made of, with each card saying which of those it came from. It
 * renders nothing at all when there is nothing to draw on, when the read fails, or when everything it would
 * suggest is already listed, so the tab is unchanged for anyone it has nothing to offer.
 *
 * It owns the three pieces of state both surfaces share: what has already been added, what is in flight, and
 * the one details modal. The panel behind More runs its own read but defers all of that to here, so adding
 * something in the panel also takes the card out of the strip underneath it.
 */
const SuggestedProductsStrip: React.FC<ISuggestedProductsStripProps> = ({storefront, onProductsAdded}) => {
    // UX gate only, and a real one here: without this permission the suggestions read itself is refused, so the
    // strip would only produce a 403. Hoisted rather than called inline in JSX because CAN uses useSelector
    // internally, and calling it under a branch would break hook order.
    const canStorefrontAdmin = useCan(STOREFRONT_ADMIN);
    const [addedIds, setAddedIds] = useState<string[]>([]);
    const [pendingId, setPendingId] = useState<string | undefined>();
    const [addingMany, setAddingMany] = useState<boolean>(false);
    const [panelOpen, setPanelOpen] = useState<boolean>(false);
    const [detailsTarget, setDetailsTarget] = useState<IDetailsTarget | undefined>();
    const [bulkList] = useBulkListOnStorefrontMutation();
    // How wide the row actually is decides how many cards there are, so the strip never scrolls sideways.
    const {rowRef, count: cardCount} = useFittedCardCount(CARD_WIDTH, CARD_GAP);

    // What they sell and what their other stores already carry, and only what is not already on this one.
    const suggestions = useGetStorefrontProductSuggestionsQuery(
        {
            storefront,
            size: SUGGESTION_STRIP_SIZE,
            excludeListed: true,
            sources: SUGGESTION_SOURCES,
            // Out of stock is said on the card rather than hidden: a product they sell constantly is worth
            // shelving whether or not a distributor happens to have it this morning.
            includeOutOfStock: true,
            // bundleSize 0 asks the API to skip the bundle passes: this tab only reads products, and the
            // Bundles tab pays for its own suggestions.
            bundleSize: 0,
        },
        // Refetch on every mount: the added-card bookkeeping is local state, so serving the cached read after
        // a navigate-away-and-back would resurrect cards for products that are already on the store.
        {skip: !storefront || !canStorefrontAdmin, refetchOnMountOrArgChange: true}
    );

    const rememberAdded = (ids: string[]) => {
        setAddedIds((current) => {
            const fresh = ids.filter((id) => !current.includes(id));
            return fresh.length > 0 ? [...current, ...fresh] : current;
        });
    };

    /**
     * List one product on this storefront, straight off its card, at whatever markup it inherits.
     *
     * A single in-flight id at a time keeps a double click from sending the same product twice.
     */
    const addProduct = async (product: IStorefrontSuggestedProduct, catalog?: string) => {
        if (pendingId || addingMany || addedIds.includes(product.id)) return;
        const label = product.name ?? 'this product';
        if (!catalog) {
            toast.error(NO_CATALOG, {autoClose: 4000});
            return;
        }
        setPendingId(product.id);
        const toastId = toast.loading(`Adding ${label} to the store...`);
        try {
            const result = await bulkList({
                catalog,
                request: suggestionListingBody(product.id, storefront),
            }).unwrap();
            const failure = listingFailureMessage(result, ADD_FAILED);
            if (failure !== undefined) throw new Error(failure);
            toast.update(toastId, {
                type: 'success',
                render: `Added ${label} to the store`,
                isLoading: false,
                autoClose: 3000,
            });
            rememberAdded([product.id]);
            onProductsAdded();
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: addErrorMessage(error, ADD_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
        }
        setPendingId(undefined);
    };

    /**
     * List everything the panel has ticked, in one call, each at its own markup.
     *
     * Resolves with the ids that actually landed so the panel can clear exactly those rows. A batch the server
     * only partly accepted is reported as such rather than rounded up to success, because the products it
     * refused are still sitting there unlisted.
     */
    const addProducts = async (entries: ISuggestionListingEntry[], catalog?: string): Promise<string[]> => {
        if (entries.length === 0 || pendingId || addingMany) return [];
        if (!catalog) {
            toast.error(NO_CATALOG, {autoClose: 4000});
            return [];
        }
        setAddingMany(true);
        const count = `${entries.length} product${entries.length === 1 ? '' : 's'}`;
        const toastId = toast.loading(`Adding ${count} to the store...`);
        try {
            const result = await bulkList({
                catalog,
                request: multiListingBody(entries, storefront),
            }).unwrap();
            const summary = multiListingSummary(result, entries.map((entry) => entry.id), ADD_MANY_FAILED);
            toast.update(toastId, {
                type: summary.outcome,
                render: summary.message,
                isLoading: false,
                autoClose: summary.outcome === 'success' ? 3000 : 6000,
            });
            if (summary.addedIds.length > 0) {
                rememberAdded(summary.addedIds);
                onProductsAdded();
            }
            return summary.addedIds;
        } catch (error) {
            toast.update(toastId, {
                type: 'error',
                render: addErrorMessage(error, ADD_MANY_FAILED),
                isLoading: false,
                autoClose: 4000,
            });
            return [];
        } finally {
            setAddingMany(false);
        }
    };

    const products = suggestions.data?.products ?? [];
    const catalog = suggestions.data?.catalog;
    const visible = products.filter((p) => !addedIds.includes(p.id));

    // Nothing to say, so say nothing: no spinner, no empty state, no shift in the tab's layout.
    if (!canStorefrontAdmin) return null;
    if (suggestions.isLoading || suggestions.isError || products.length === 0) return null;

    return (
        <Stack tokens={{childrenGap: '0.5em'}} styles={{root: {marginBottom: '1em'}}}>
            {/* No subtitle: the cards now each say why they are here, and one blanket reason for a mixed list
              * would be wrong for most of them. "See all" lives up here rather than as a tile at the end of the
              * row, where it was the first thing to be pushed off the edge. */}
            <Stack horizontal verticalAlign='center' horizontalAlign='space-between' tokens={{childrenGap: '0.5em'}}>
                <Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>Suggested Products</Text>
                <ActionButton
                    text='See all'
                    iconProps={{iconName: 'ChevronRight'}}
                    title='See all suggested products'
                    onClick={() => setPanelOpen(true)}
                    styles={{root: {height: 28, padding: 0, flexShrink: 0}}}
                />
            </Stack>

            {/* One row, only as many cards as fit. The hidden overflow is a safety net for the frame between a
              * resize and the remeasure, not the way anything is meant to be reached. */}
            <div ref={rowRef} style={{overflow: 'hidden'}}>
                <Stack horizontal verticalAlign='stretch' tokens={{childrenGap: CARD_GAP}}>
                    {visible.slice(0, cardCount).map((product) => (
                        <SuggestionCard
                            key={product.id}
                            product={product}
                            pending={pendingId === product.id}
                            // No catalog means the bulk listing route has no catalog segment to call, and one
                            // add already running holds the rest so a burst of clicks cannot queue up behind it.
                            disabled={!catalog || addingMany || (!!pendingId && pendingId !== product.id)}
                            onAdd={() => addProduct(product, catalog)}
                            onOpenDetails={catalog ? () => setDetailsTarget({id: product.id, catalog}) : undefined}
                        />
                    ))}
                </Stack>
            </div>

            {panelOpen && (
                <SuggestedProductsPanel
                    storefront={storefront}
                    addedIds={addedIds}
                    busy={addingMany || !!pendingId}
                    onOpenDetails={(id, productCatalog) => setDetailsTarget({id, catalog: productCatalog})}
                    onAddSelected={addProducts}
                    onDismiss={() => setPanelOpen(false)}
                />
            )}

            {detailsTarget && (
                <DetailsModal
                    product={detailsTarget.id}
                    catalog={detailsTarget.catalog}
                    open={true}
                    onClose={() => setDetailsTarget(undefined)}
                />
            )}
        </Stack>
    );
};

export default SuggestedProductsStrip;
